import React, { ChangeEvent, useEffect, useState } from "react";
import Sidebar from "components/sidebar";
import { getRequestTypeText, getStoredUserData, getUserSession, isAdmin, isAlphaNumeric, isAuthenticated, isStaff, isDriver } from "utils/utils";
import { useNavigate } from "react-router-dom";
import RequestTable from "./requests_table";
import PageControl from "components/page_control";
import SearchBox from "components/search_box";
import { ApiClient } from "utils/apiclient";
import Spinner from "components/spinner";
import { RequestService } from "utils/services/request.service";
import TopStrip from "components/top_strip";
import { toast } from 'react-toastify'
import RequestApprovalDialog from "./request_approval_dialog";
import { IVehicleRequest, IMaintenanceRequest, IFuelRequest, User, ISelectOption, Vehicle } from "utils/types";
import { REQUEST_TYPES, REQUESTS_STATUS_ID } from "utils/constants";
import { DateTime } from "luxon";
import { get } from "http";

const Requests = (_props: any) => {
    const navigate = useNavigate();
    const userData = getStoredUserData();
    const userSession = getUserSession()
    const apiClient = new ApiClient(userSession?.accessToken);
    const requestService = new RequestService(apiClient)
    const [requests, setRequests] = useState<any[]>([])
    const [filteredRequests, setFilteredRequests] = useState(requests)
    const [pageInfo, setPageInfo] = useState<any>({ totalPages: 0, totalCount: 0, currentPage: 1, pageSize: 15, hasNext: false, hasPrevious: false });
    const [pageSize, setPageSize] = useState<number>(20)
    const [page, setPage] = useState<number>(1)
    const [loading, setLoading] = useState<boolean>(false)
    const [selectedRequest, setSelectedRequest] = useState<IVehicleRequest | IMaintenanceRequest | IFuelRequest>()
    const [type, setType] = useState<string>()
    const [open, setOpen] = useState<boolean>(false)
    const [reqType, setReqType] = useState<number>(isDriver(userData) ? 1 : 0)
    const [driverOptions, setDriverOptions] = useState<ISelectOption[]>([])
    const [vehicleOptions, setVehicleOptions] = useState<ISelectOption[]>([])
    const [filterOption, setFilterOption] = useState<string | undefined | number>('')
    const [searchOption, setSearchOption] = useState<ISelectOption | null>(null)
    const [userOptions, setUserOptions] = useState<ISelectOption[]>([])
    const [apiQuery, setApiQuery] = useState<string>('');


    const handleClickNew = (e: React.MouseEvent<HTMLButtonElement>) => {
        const id = e.currentTarget.id;
        let path = 'vehicle';
        switch (id) {
            case 'btnVehicle':
            default:
                path = 'vehicle';
                break;
            case 'btnFuel':
                path = 'fuel';
                break;
            case 'btnService':
                path = 'service';
                break;

        }
        navigate(`/requests/${path}/new`)
    }

    const handleRequestChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const type = Number(e.currentTarget.value)
        setReqType(type);
        setPage(1)
        if (filterOption && searchOption) {
            const query = filterOption === 'vehicle' ? `?vehicle=${searchOption?.label}` : (filterOption === 'driver' ? `?driver=${searchOption?.value}` : `?date=${searchOption?.value}`)
            searchApi(query, type)
        }
        else getRequests(pageSize, 1, type);
    }
    const getRequests = async (pageSize: number, page: number, requestType: number) => {
        setLoading(true);

        const response = await requestService.getUserRequests(requestType, { page: page, take: pageSize });
        setLoading(false)
        if (response.status === 200) {
            const data = response.data;
            setPageInfo({ totalPages: response.data.totalPages, totalCount: response.data.totalCount, currentPage: response.data.page, pageSize: response.data.pageSize, hasNext: response.data.hasNext, hasPrevious: response.data.hasPrevious })
            setRequests(data.requests);
            setFilteredRequests(data.requests);
            setFilterOption('')
        }
    }
    const getPreviousPage = () => {
        setPage(page - 1)
        setFilterOption('')
    }
    const getNextPage = () => {
        setPage(page + 1);
        setFilterOption('')
    }
    const handlePageSizeChange = (pageSize: number) => {
        if (pageSize && pageSize >= 10) {
            setPageSize(pageSize);
            setPage(1)
        }
        else {
            setPageSize(10);
            setPage(1);
        }
        setFilterOption('')
    }

    const handleFilter = (option: ISelectOption) => {
        if (filterOption === 'driver') {
            const result = requests.filter((req) => {
                return (
                    (reqType === REQUEST_TYPES.FUEL || reqType === REQUEST_TYPES.NORMAL_SERVICE || reqType === REQUEST_TYPES.REPAIR || reqType === REQUEST_TYPES.FULL_SERVICE) && req?.requestor_detail?.id === option.value ||
                    reqType === REQUEST_TYPES.VEHICLE && req?.driver === option.value
                )
            });
            setFilteredRequests(result);
        }
        else if (filterOption === 'vehicle') {
            const result = requests.filter((req) => {
                return (req?.vehicle_detail?.id === option.value || req?.vehicle === option.label)
            });
            setFilteredRequests(result);
        }
        else if (filterOption === 'date') {
            const startDate = DateTime.fromMillis(Date.parse(String(option.value))).startOf('day').toSeconds();
            const endDate = DateTime.fromMillis(Date.parse(String(option.value))).endOf('day').toSeconds();

            const result = requests.filter((req) => {
                return (req?.date_created >= startDate && req?.date_created <= endDate)
            });
            setFilteredRequests(result);
        }
        else if(filterOption === 'requestor'){
            const result = requests.filter((req) => {
                return req?.requestor === option.value
            });
            setFilteredRequests(result);
        }
        else setFilteredRequests(requests);
        setSearchOption(option)
    }
    const handleSelect = (item: IVehicleRequest | IMaintenanceRequest | IFuelRequest, type: string) => {
        if (type === 'view') {
            let requestType: string = 'vehicle';
            switch (item.type) {
                case 1:
                    requestType = 'fuel';
                    break;
                case 2:
                case 3:
                case 4:
                    requestType = 'service';
                    break;
                case 0:
                default:
                    requestType = 'vehicle'

            }
            navigate(`/requests/${requestType}/view/${item.id}?t=${item.type}`, { state: { request: item } })
        }
        else {
            setSelectedRequest(item);
            setType(type);
            setOpen(true)
        }

    }
    const handleSubmit = async (data: any) => {
        if (type === 'approve') await approveRequest(data);
        else if (type === 'reject') await rejectRequest(data)
    }
    const approveRequest = async (formData: any) => {
        let data: any = {};
        if (userData) {
            switch (selectedRequest?.type) {
                case 0:
                    data = {
                        ...selectedRequest,
                        remark: formData.remark
                    };
                    break;
                case 1:
                    data = {
                        ...selectedRequest,
                        nameOfApprover: `${userData.fname} ${userData.lname}`,
                        status: REQUESTS_STATUS_ID.APPROVED,
                        attender: userData?.id,
                        id: selectedRequest.id,
                        type: selectedRequest.type,
                        vehicle: selectedRequest.vehicle,
                        remark: formData.remark
                    };
                    break;
                case 2:
                case 3:
                case 4:
                    data = {
                        ...selectedRequest,
                        nameOfApprover: `${userData.fname} ${userData.lname}`,
                        status: REQUESTS_STATUS_ID.APPROVED,
                        attender: userData?.id,
                        id: selectedRequest?.id,
                        type: selectedRequest?.type,
                        vehicle: selectedRequest?.vehicle,
                        remark: formData.remark
                    }
            }

            try {
                setLoading(true)
                const req = await requestService.processRequest(data, userData, true);
                toast.info(req.data.response, {
                    position: "top-center",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                })
                if (apiQuery != '') searchApi(apiQuery, reqType);
                else getRequests(pageSize, page, reqType);
            }
            catch (e: any) {
                toast.error("Something went wrong, please contact support", {
                    position: "top-center",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                })
            }
            finally {
                setLoading(false)

            }
        }

    }
    const rejectRequest = async (formData: any) => {
        if (userData) {
            const data: any = {
                ...selectedRequest,
                remark: formData.remark,
                attended_by: userData.id,
                closed_by: userData.id,
                nameOfAttender: `${userData.fname} ${userData.lname}`
            }
            setLoading(true)
            try {
                const axios = await requestService.processRequest(data, userData, false)
                console.log("🚀 ~ sendApproval ~ axios:", axios.data)

            }
            catch (e: any) {
                console.log("🚀 ~ sendApproval ~ e:", e)
                alert("Something went wrong")
            }
            finally {
                if (apiQuery != '') searchApi(apiQuery, reqType);
                else getRequests(pageSize, page, reqType);
                setLoading(false)
            }
        }

    }
    const searchApi = async (query: string, type?: number) => {
        setLoading(true)
        const response = await requestService.searchRequests(type || reqType, query, { page: page, take: pageSize })
        const data = response.data;
        setPageInfo({ totalPages: response.data.totalPages, totalCount: response.data.totalCount, currentPage: response.data.page, pageSize: response.data.pageSize, hasNext: response.data.hasNext, hasPrevious: response.data.hasPrevious })
        setRequests(data.requests);
        setFilteredRequests(data.requests);
        setFilterOption(filterOption)
        setLoading(false)
    }
    const getActiveDrivers = async () => {
        const drivers = await apiClient.getActiveDrivers();
        const options = drivers.map((driver: User) => {
            return {
                value: driver.id, label: `${driver.fname} ${driver.lname}`
            }
        })
        setDriverOptions(options)
    }
    const getActiveUsers = async () => {
      let users = [userData] as unknown as User[];
      if (isAdmin(userData)) {
        const response = await apiClient.getActiveUsers();
        users = response.users;
        setUserOptions(users.map((user: User) => ({ value: user.id, label: `${user.fname} ${user.lname}` })));
      }

    };
    const getActiveVehicles = async () => {
        const req = await apiClient.getVehicles();
        const options = req.vehicles.map((vehicle: Vehicle) => {
            return {
                value: vehicle.id, label: vehicle.pnumber
            }
        })
        setVehicleOptions(options)
    }
    const handleSearch = (option: ISelectOption) => {
        setSearchOption(option)
        let query = "";
        if (!option) return;
        if (option.label === 'date' && typeof option.value === 'object') {
            const startDate = DateTime.fromJSDate(new Date(option.value?.startDate)).toFormat('yyyy-MM-dd');
            const endDate = DateTime.fromJSDate(new Date(option.value?.endDate)).toFormat('yyyy-MM-dd');
            query = `?startDate=${ startDate }&endDate=${ endDate }`;
        }
        else if (filterOption === 'vehicle') query = `?vehicle=${ option?.value }`;
        else if (filterOption === 'driver') query = `?driver=${ option?.value }`;
        else if (filterOption === 'requestor') query = `?requestor=${ option?.value }`;
        setApiQuery(query);
        searchApi(query)
        
    }
    const handleFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const value = e.currentTarget.value;
        setFilterOption(value);
        
        if (value === 'driver') {

            setSearchOption(driverOptions[0]);
        }
        else if (value === 'requestor') {
            setSearchOption(userOptions[0]);
        }
        else if (value === 'vehicle') {
            setSearchOption(vehicleOptions[0]);
        }
        else if (value === 'date') {
            setSearchOption({ label: '', value: new Date().toDateString() });
        } 

    }
    useEffect(() => {
        if (isAuthenticated()) {
            if (filterOption && searchOption) {
                let query = "";
                switch (filterOption) {
                    case 'requestor':
                        query = `?requestor=${searchOption.value}`;
                        break;
                    case 'vehicle':
                        query = `?vehicle=${searchOption.label}`;
                        break;
                    case 'driver':
                        query = `?driver=${searchOption.value}`;
                        break;
                    case 'date':
                        query = `?date=${searchOption.value}`;
                        break;
                }
                searchApi(query)
            }
            else getRequests(pageSize, page, reqType);
            if(!isDriver(userData)) getActiveDrivers();
            getActiveVehicles()
            getActiveUsers();
        }
        else navigate('/login', { state: { targetPath: '/requests' } })
    }, [pageSize, page])

    return (
      <>
        <div className='w-full flex justify-end items-center bg-gray-100 pe-4'>
          <TopStrip user={userData} />
        </div>
        <main className='w-full flex bg-white min-h-screen'>
          <div className='md:w-2/12 flex flex-col items-start text-center justify-center'>
            <Sidebar
              notificationCount={[].length}
              user={userData}
              active={1}
            />{' '}
            f
          </div>

          <div className='md:w-9/12 mx-auto space-y-4 mt-2'>
            {selectedRequest && open ? (
              <RequestApprovalDialog
                data={selectedRequest}
                type={type}
                state={open}
                onClose={() => {
                  setOpen(false);
                }}
                onSubmit={handleSubmit}
              />
            ) : null}
            <div className='flex space-x-2 w-full my-2 pb-2 items-center justify-between border-b-2'>
              <div className='w-full flex items-center justify-start space-x-2'>
                {!isDriver(userData) ? (
                  <button
                    className='bg-primaryLight text-textDefault hover:bg-accentDark hover:text-white px-2 py-2 w-2/12'
                    onClick={handleClickNew}
                    id='btnVehicle'>
                    Vehicle
                  </button>
                ) : null}
                {isDriver(userData) || isAdmin(userData) ? (
                  <>
                    <button
                      className='bg-primaryLight text-textDefault hover:bg-accentDark hover:text-white px-2 py-2 w-2/12'
                      onClick={handleClickNew}
                      id='btnFuel'>
                      Fuel
                    </button>
                    <button
                      className='bg-primaryLight text-textDefault hover:bg-accentDark hover:text-white px-2 py-2 w-2/12'
                      onClick={handleClickNew}
                      id='btnService'>
                      Service
                    </button>
                  </>
                ) : null}
              </div>
            </div>
            <div className='w-full text-textDefault mt-2'>
              <div className='w-full flex items-center justify-between pt-2'>
                <p className='text-2xl font-bold text-start w-6/12'>
                  {getRequestTypeText(reqType)}
                </p>

                <form className='md:w-3/12 flex-col'>
                  {/* <label>Request Type</label> */}
                  <select
                    className='form-control w-full'
                    onChange={handleRequestChange}>
                    <option
                      value={-1}
                      disabled>
                      Select Request Type
                    </option>
                    <option
                      value={0}
                      selected={reqType === 0}>
                      Vehicle Requests
                    </option>
                    {!isStaff(userData) ? (
                      <>
                        <option
                          value={1}
                          selected={reqType === 1}>
                          Fuel Requests
                        </option>
                        <option
                          value={2}
                          selected={reqType === 2}>
                          Maintenance Request
                        </option>
                      </>
                    ) : null}
                  </select>
                </form>
              </div>
              <div className='flex justify-end items-center w-full'>
                <div className='text-left'>
                  <label className=''>Filter by {filterOption}</label>
                  <select
                    className='form-control'
                    onChange={handleFilterChange}
                    defaultValue={filterOption}>
                    <option value='_'>select</option>
                    {!isDriver(userData) ? (
                      <option value='driver'>Driver</option>
                    ) : null}
                    {!isDriver(userData) ? (
                      <option value='requestor'>Requestor</option>
                    ) : null}
                    <option value='vehicle'>Vehicle</option>
                    <option value='date'>Date</option>
                  </select>
                </div>

                {filterOption !== '' && (
                  <SearchBox
                    className='w-full md:w-4/12 flex space-x-2 my-4 items-center justify-end pt-6'
                    onChange={handleFilter}
                    onSearch={handleSearch}
                    options={
                      filterOption === 'driver'
                        ? driverOptions
                        : filterOption === 'vehicle'
                        ? vehicleOptions
                        : filterOption === 'requestor'
                        ? userOptions
                        : []
                    }
                    defaultValue={new Date().toDateString()}
                  />
                )}
              </div>
              <div className='w-full border-top flex-row flex-start bg-main-color align-items-center '>
                <PageControl
                  pageSize={pageSize}
                  onNext={getNextPage}
                  onPrevious={getPreviousPage}
                  currentPage={page}
                  totalPages={pageInfo.totalPages}
                  onPageSizeChange={(pageSize: number) => {
                    handlePageSizeChange(pageSize);
                  }}
                  hasNext={pageInfo.hasNext}
                  hasPrevious={pageInfo.hasPrevious}
                />
              </div>
              {loading ? (
                <Spinner className='spinner-md mt-6' />
              ) : (
                <RequestTable
                  data={filteredRequests}
                  user={userData}
                  onSelect={handleSelect}
                />
              )}
            </div>
          </div>
        </main>
      </>
    );
}
export default Requests;
